import type { Product } from '@noths/polaris-design-system-ooux-interfaces';

/**
 * Narrows a product to its first image, so a card in a carousel browses no
 * images of its own.
 *
 * `ProductCard` shows a product's images as a horizontal scroller, which inside
 * a carousel is a second one nested in the first. Both declare
 * `overscroll-behavior-x: contain`, so a swipe over the image area moves the
 * card's images and then stops dead rather than carrying on to the next product,
 * and the carousel cannot be swiped over that area at all. Half the card's height
 * at the card's full width, and the obvious place to put a thumb.
 *
 * Handing the card one image removes the conflict rather than arbitrating it: a
 * single-image track has nothing to scroll, so it is not a scroll container and
 * the swipe reaches the carousel untouched. No gesture rules, nothing passed
 * between the two components, and the card is unchanged.
 *
 * The cost is that image browsing is a grid feature, not a carousel one. That is
 * the intended trade: a swipe in a carousel is ambiguous in a way a swipe in a
 * grid is not, so the grid is also the better place to learn whether customers
 * use it at all.
 *
 * A product with no image is returned untouched. There is nothing to browse
 * either way, and the card already renders its own placeholder.
 */
export const withSingleImage = (product: Product): Product => {
  const firstImage = product.media.find(({ type }) => type === 'image');

  return firstImage ? { ...product, media: [firstImage] } : product;
};
